/**
 * Guards for URLs that come from the API and end up in a navigation sink
 * (`window.location.href = ...`, `window.open(...)`). A compromised or
 * misconfigured backend must not be able to send the browser to a
 * `javascript:` URL or to an unexpected host.
 */

/** Hosts Google's OAuth consent screen is served from. */
export const GOOGLE_OAUTH_HOSTS = ["accounts.google.com"] as const;

/**
 * Parses `value` as an absolute https URL. Returns null for anything else:
 * relative or scheme-relative values, other schemes (`http:`, `javascript:`,
 * `data:` ...), URLs with embedded credentials, or values that do not parse.
 */
export function parseHttpsUrl(value: unknown): URL | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  // Absolute only: refuse "//host" and anything without an explicit scheme.
  if (!/^https:\/\//i.test(trimmed)) return null;
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" || !url.hostname) return null;
  if (url.username || url.password) return null;
  return url;
}

/**
 * The URL as a string when it is an absolute https URL whose host is one of
 * `allowedHosts` (exact match, case-insensitive); otherwise null.
 */
export function safeHttpsUrl(value: unknown, allowedHosts?: readonly string[]): string | null {
  const url = parseHttpsUrl(value);
  if (!url) return null;
  if (allowedHosts && !allowedHosts.some((h) => h.toLowerCase() === url.hostname.toLowerCase())) {
    return null;
  }
  return url.href;
}

/**
 * `path` when it is a same-origin absolute path ("/", "/login?x=1"), else
 * `fallback`. Rejects scheme-relative ("//evil.com") and backslash tricks
 * ("/\\evil.com", "\\\\evil.com") that browsers treat as another origin, and
 * any value carrying control characters.
 */
export function safeAppPath(path: unknown, fallback = "/"): string {
  if (typeof path !== "string") return fallback;
  if (!path.startsWith("/")) return fallback;
  if (path.startsWith("//") || path.includes("\\")) return fallback;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(path)) return fallback;
  return path;
}

/**
 * Opens an https URL in a new tab without giving it a handle on this window.
 * Returns false (and opens nothing) when the URL fails the check.
 */
export function openExternalHttps(value: unknown, allowedHosts?: readonly string[]): boolean {
  const href = safeHttpsUrl(value, allowedHosts);
  if (!href) return false;
  window.open(href, "_blank", "noopener,noreferrer");
  return true;
}
